import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Header from "../../components/Header";
import SideBar from "../../components/Sidebar";
import "../../../node_modules/bootstrap/dist/css/bootstrap.min.css";
import "../../../node_modules/bootstrap/dist/js/bootstrap.min.js";
import { ASK_BENEFITS_BOT, START_BENEFITS } from "../../utils/constants";
import BouncingDotsLoader from "../../components/LoadingDots";
import Chat, { ChatMessage } from "../../components/Chat";
import ChatComposer from "../../components/ChatComposer";
import GuestBenefitsPrompt, {
  GuestBenefitsDetails,
} from "../../components/GuestBenefitsPrompt";
import useWindowWidth from "../../utils/windowWidth";
import {
  getBenefitsGuestAwareHeaders,
  getGuestId,
  storeGuestIdFromHeaders,
} from "../../utils/guestSession";

const BENEFITS_GUEST_STARTED_KEY = "ncludeGuestLoginPrompt:benefits-details";

function InfoAccess() {
  const mainRef = useRef<HTMLHeadingElement>(null);
  const location = useLocation();
  const isAuthenticated = Boolean(localStorage.getItem("token"));
  const [benefitsReady, setBenefitsReady] = useState(
    () =>
      isAuthenticated ||
      Boolean(
        getGuestId() && sessionStorage.getItem(BENEFITS_GUEST_STARTED_KEY) === "true"
      )
  );

  const [chat, setChat] = useState<ChatMessage[]>([
    {
      text: "Hello! I am your Benefits Screening Assistant. Ready to begin?",
      timestamp: new Date().toISOString(),
      sender: "bot",
    },
  ]);
  const [prompt, setPrompt] = useState("");
  const [sendDisabled, setSendDisabled] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [uuid] = useState(() => crypto.randomUUID());

  const inputRef = useRef<HTMLTextAreaElement>(null);

  const isInitialGreeting = chat.length === 1 && chat[0].sender === "bot";

  const handleGetStarted = () => {
    if (!benefitsReady || sendDisabled || isStreaming) return;
    const messageText = "Lets start";
    setSendDisabled(true);
    setChat((prev) => [
      ...prev,
      {
        text: messageText,
        timestamp: new Date().toISOString(),
        sender: "user",
      },
    ]);
    sendChatMessage(messageText);
  };

  function handlePromptSubmit(): void {
    if (!benefitsReady) return;
    if (isInitialGreeting) {
      handleGetStarted();
      return;
    }
    const messageText = prompt.trim();
    if (!messageText || sendDisabled) return;
    setSendDisabled(true);
    setChat((chat) => [
      ...chat,
      {
        text: messageText,
        timestamp: new Date().toISOString(),
        sender: "user",
      },
    ]);
    sendChatMessage(messageText);
    setPrompt("");
  }

  /*
  const fetchChats = () => {
    const url = FETCH_BENEFITS_CHATS;
    makeAuthenticatedGet(url)
      .then((response) => {
        console.log("chats", response);
        setChatId(response[0].sessionId);
        response[0].messages.map((msg: { text: string }) => {
          setChat((chat) => [...chat, msg.text]);
          return "";
        });
      })
      .catch((error) => {
        console.log("cannot fetch chats", error);
      });
  };
  */

  const sendChatMessage = async (messageText: string) => {
    try {
      const response = await fetch(ASK_BENEFITS_BOT, {
        method: 'POST',
        headers: getBenefitsGuestAwareHeaders(),
        body: JSON.stringify({ message: messageText, uuid, stream: true })
      });

      storeGuestIdFromHeaders(response.headers);

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }
      if (!response.body) {
        throw new Error("The response did not include a message stream");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let isFirstChunk = true;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);

        if (isFirstChunk) {
          setIsStreaming(true);
          setChat((prev) => [...prev, {
            text: chunk,
            timestamp: new Date().toISOString(),
            sender: "bot",
          }]);
          isFirstChunk = false;
        } else {
          setChat((prev) => {
            const updated = [...prev];
            updated[updated.length - 1].text += chunk;
            return updated;
          });
        }
      }

    } catch (error) {
      console.error("Error fetching data:", error);
      setChat((prev) => [...prev, {
        text: "I’m sorry, I couldn’t complete that response. Please try again in a moment.",
        timestamp: new Date().toISOString(),
        sender: "bot",
      }]);
    } finally {
      setSendDisabled(false);
      setIsStreaming(false);
    }
  };

  const startGuestBenefits = async (details: GuestBenefitsDetails) => {
    const response = await fetch(START_BENEFITS, {
      method: "POST",
      headers: getBenefitsGuestAwareHeaders(),
      body: JSON.stringify(details),
    });
    storeGuestIdFromHeaders(response.headers);

    if (!response.ok) {
      let message = "We couldn’t start your benefits screening. Please try again.";
      try {
        const result = await response.json();
        message = result?.message || result?.error || result?.data?.message || message;
      } catch {
        // Keep the fallback when the backend does not return a JSON error.
      }
      throw new Error(message);
    }

    if (!getGuestId()) {
      throw new Error("We couldn’t establish your guest session. Please try again.");
    }

    sessionStorage.setItem(BENEFITS_GUEST_STARTED_KEY, "true");
    setBenefitsReady(true);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  useEffect(() => {
    document.title = "Benefits - Nclude Portal";
  }, []);

  const renderMessageActions = (message: ChatMessage, index: number) => {
    if (isInitialGreeting && index === 0 && message.sender === "bot") {
      return (
        <div className="benefits-message-actions">
          <button
            type="button"
            className="benefits-get-started-btn"
            onClick={handleGetStarted}
            disabled={!benefitsReady || sendDisabled || isStreaming}
            aria-label="Get started with Benefits Screening Assistant"
          >
            {sendDisabled ? "Starting…" : "Get started"}
          </button>
        </div>
      );
    }
    return null;
  };

  const liveRegionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Set focus on the main content when the route changes
    if (mainRef.current) {
      mainRef.current.focus();
    }
  }, [location]);

  //window size
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 769;

  // if (!socket) {
  //   return <div>Loading...</div>;
  // }

  return (
    <div className="containerr">
      {!isAuthenticated && (
        <GuestBenefitsPrompt
          show={!benefitsReady}
          onContinue={startGuestBenefits}
        />
      )}
      {!isMobile && <Header />}
      <div className="page-container">
        <SideBar activePage="Benefits" />
        <main className="doc-container agent-chat-page" id="main-content" tabIndex={-1}>
          <div className="agent-chat-heading">
            <div>
              {/* <p className="agent-eyebrow">Personalized guidance</p> */}
              <h1 id="benefits-heading" className="display-title" ref={mainRef} tabIndex={-1}>Benefits</h1>
              <p className="agent-description">Find federal and state programs that may support you and your household.</p>
            </div>
            <p className="agent-status" role="status" aria-live="polite">
              <span className={sendDisabled || isStreaming ? "status-dot busy" : "status-dot"} aria-hidden="true" />
              {!benefitsReady ? "Getting started" : sendDisabled || isStreaming ? "Responding" : "Online"}
            </p>
          </div>

          <aside className="agent-overview" aria-labelledby="benefits-overview-title">
            <div>
              <h2 id="benefits-overview-title">How your screening works</h2>
              <p>Available across all 50 US states, Washington DC, and US territories.</p>
            </div>
            <ol>
              <li><strong>Answer guided questions</strong><span>Tell us about your disability, work, income, household, and housing.</span></li>
              <li><strong>Receive a tailored report</strong><span>Review federal and state benefit programs that may match your situation.</span></li>
            </ol>
          </aside>

          <section className="chat-box">
            <div
              className="chat-window-container"
              aria-busy={sendDisabled}
            >
              {chat.length === 0 ? <BouncingDotsLoader /> : (
                <Chat
                  chat={chat}
                  isStreaming={isStreaming}
                  assistantName="Benefits Assistant"
                  announcementRef={liveRegionRef}
                  renderMessageActions={renderMessageActions}
                />
              )}
              {sendDisabled && !isStreaming && (
                <div className="assistant-thinking" role="status">
                  <BouncingDotsLoader />
                  <span>Benefits Assistant is thinking…</span>
                </div>
              )}
            </div>
            <div ref={liveRegionRef} aria-live="polite" aria-atomic="true" className="visually-hidden" />
            <ChatComposer
              inputRef={inputRef}
              value={prompt}
              onChange={setPrompt}
              onSubmit={handlePromptSubmit}
              disabled={!benefitsReady || isInitialGreeting || sendDisabled}
              busy={sendDisabled}
              placeholder={
                !benefitsReady
                  ? "Complete the introduction to continue"
                  : isInitialGreeting
                  ? "Click 'Get started' above to begin"
                  : "Message the Benefits Assistant"
              }
              assistantName="Benefits Assistant"
              helpText={isInitialGreeting ? "Please click 'Get started' above to begin your screening." : undefined}
            />
          </section>
        </main>
      </div>
    </div>
  );
}

export default InfoAccess;
